import { useEffect } from 'react';
import { useSEO } from '../../utils/seo';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import DebtCalculator from '../../components/feature/DebtCalculator';
import ExitIntentPopup from '../../components/feature/ExitIntentPopup';

export default function BusinessDebtRelief() {
  useSEO({
    title: 'Business Debt Relief Services | MCA & Commercial Debt Settlement | Brite Solutions',
    description: 'Professional business debt relief for merchant cash advances, business loans, and commercial debt. Restore cash flow and eliminate overwhelming business debt. Free consultation for business owners.',
    keywords: 'business debt relief, merchant cash advance relief, MCA debt settlement, commercial debt negotiation, business loan settlement, business debt restructuring, cash flow restoration',
    canonical: '/business-debt-relief',
    structuredData: {
      '@type': 'Service',
      name: 'Business Debt Relief',
      provider: {
        '@type': 'Organization',
        name: 'Brite Solutions',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '1247'
        }
      },
      description: 'Professional business debt relief services for merchant cash advances, business loans, and commercial debt',
      areaServed: 'United States',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Free consultation for business owners'
      }
    }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <ExitIntentPopup />

      {/* Enhanced SEO Meta Tags */}
      <title>Business Debt Relief Services - Commercial Debt Settlement | Brite Solutions</title>
      <meta name="description" content="Professional business debt relief and commercial debt settlement services. Resolve merchant cash advances, business loans, and vendor debts with expert negotiation. Free business consultation available." />
      <meta name="keywords" content="business debt relief, commercial debt settlement, merchant cash advance relief, business debt negotiation" />
      <link rel="canonical" href="https://britesolutions.com/business-debt-relief" />
      
      {/* Business Debt Relief Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Business Debt Relief Services",
          "description": "Professional debt relief services for businesses struggling with commercial debt, merchant cash advances, and vendor obligations.",
          "provider": {
            "@type": "FinancialService",
            "name": "Brite Solutions"
          },
          "areaServed": "US",
          "serviceType": "Business Debt Relief"
        })}
      </script>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 to-gray-700 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Business Debt Relief Solutions
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Eliminate merchant cash advance debt, business loans, and commercial debt. Restore your cash flow and save your business with our specialized business debt relief services.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => document.querySelector<HTMLElement>('#vapi-widget-floating-button')?.click()}
                className="bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Get Free Business Consultation
              </button>
              <a
                href="tel:+18005551234"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">$200M+</div>
              <div className="text-gray-600">Business Debt Settled</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">450+</div>
              <div className="text-gray-600">Businesses Helped</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">50-70%</div>
              <div className="text-gray-600">Average Debt Reduction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">16+</div>
              <div className="text-gray-600">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Debt Types */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Types of Business Debt We Settle
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our business debt specialists handle all types of commercial debt
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-red-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-funds-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Merchant Cash Advances</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                High-interest MCA loans with daily payment requirements that drain your cash flow.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-red-600 mr-2 mt-1"></i>
                  <span>Daily payment MCAs</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-red-600 mr-2 mt-1"></i>
                  <span>Multiple MCA stacking</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-red-600 mr-2 mt-1"></i>
                  <span>High-factor rate MCAs</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-orange-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-building-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Business Loans</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Term loans, lines of credit, and SBA loans that have become unmanageable.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                  <span>Term loans</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                  <span>Business lines of credit</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                  <span>Equipment financing</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-green-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-yellow-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-file-list-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Commercial Debt</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Vendor debt, supplier invoices, and other commercial obligations.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-yellow-600 mr-2 mt-1"></i>
                  <span>Vendor debt</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-yellow-600 mr-2 mt-1"></i>
                  <span>Supplier invoices</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-yellow-600 mr-2 mt-1"></i>
                  <span>Business credit cards</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MCA Problem Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Trapped in the Merchant Cash Advance Cycle?
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Merchant cash advances can quickly spiral out of control with their high factor rates and daily payment requirements. Many businesses find themselves trapped in a cycle of taking new MCAs to pay off old ones.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <i className="ri-alert-line text-2xl text-red-600 mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Daily Payment Drain:</strong>
                    <p className="text-gray-700">MCAs take a percentage of your daily sales, leaving little for operations.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-alert-line text-2xl text-red-600 mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Stacking Problem:</strong>
                    <p className="text-gray-700">Multiple MCAs compound the problem, draining even more cash flow.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-alert-line text-2xl text-red-600 mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">High Costs:</strong>
                    <p className="text-gray-700">Factor rates of 1.2-1.5 mean you pay back 20-50% more than you borrowed.</p>
                  </div>
                </div>
              </div>

              <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg">
                <p className="text-gray-900 font-semibold mb-2">We Can Help You Break Free</p>
                <p className="text-gray-700">
                  Our MCA relief specialists negotiate with lenders to restructure payments, reduce balances, and restore your business cash flow.
                </p>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/99191d31e0485099374a58f81e6fc209.jpg"
                alt="Business owner dealing with MCA debt"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How Business Debt Relief Works
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our proven process helps businesses eliminate debt and restore cash flow
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold text-white">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Business Analysis</h3>
              <p className="text-gray-700">
                We review your business debts, cash flow, and financial situation.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold text-white">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Custom Strategy</h3>
              <p className="text-gray-700">
                We create a tailored debt relief plan for your specific business needs.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold text-white">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Aggressive Negotiation</h3>
              <p className="text-gray-700">
                Our specialists negotiate with lenders to reduce debt and restructure payments.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold text-white">
                4
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Cash Flow Restored</h3>
              <p className="text-gray-700">
                Your business is debt-free with restored cash flow and financial stability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <DebtCalculator />

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-gray-900 to-gray-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Save Your Business from Overwhelming Debt
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get a free business debt consultation with our specialists. No obligation, 100% confidential.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => document.querySelector<HTMLElement>('#vapi-widget-floating-button')?.click()}
              className="bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Free Business Consultation
            </button>
            <a
              href="tel:+18005551234"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
